<template>
  <div class="campaign-page campaign-editor">
    <header class="campaign-header">
      <div>
        <h1>{{ isEdit ? 'Edit campaign #' + $route.params.id : 'Create campaign' }}</h1>
        <p>Turn a promotion code into a banner or pop-up your customers will see.</p>
      </div>
      <button
        v-if="ready && isEdit"
        type="button"
        class="btn btn-outline-primary ml-auto"
        :disabled="saving"
        @click="$refs.translator.showTranslateModal()"
      >
        <i class="fa fa-language" aria-hidden="true" />
        Translations
      </button>
      <router-link class="btn btn-outline-secondary" :to="{ name: 'campaigns' }">
        Back to campaigns
      </router-link>
    </header>
    <loading v-if="loading" />
    <div v-if="errors.length" class="alert alert-danger" role="alert">
      <div v-for="(error, i) in errors" :key="i">{{ error }}</div>
      <button
        v-if="!ready && !loading"
        class="btn btn-sm btn-outline-danger"
        @click="initialize"
      >
        Retry
      </button>
    </div>
    <form v-if="ready" class="campaign-editor-form" @submit.prevent="save">
      <fieldset :disabled="saving">
        <div class="row">
          <div class="col-lg-7">
            <section class="campaign-panel campaign-form editor-section">
              <div class="editor-section-heading">
                <span class="editor-section-icon">
                  <i class="fa fa-pencil" aria-hidden="true" />
                </span>
                <div>
                  <h2>Campaign content</h2>
                  <p>Write the message your customers will see.</p>
                </div>
                <span class="editor-language">English</span>
              </div>
              <div class="form-group">
                <label for="campaign-title">Title *</label>
                <input
                  placeholder="e.g. The biggest sale of the year"
                  id="campaign-title"
                  v-model="form.title"
                  class="form-control"
                  required
                  maxlength="255"
                />
              </div>
              <div class="form-group">
                <label for="campaign-description">Description</label>
                <textarea
                  placeholder="Tell customers what makes this offer special…"
                  id="campaign-description"
                  v-model="form.description"
                  class="form-control"
                  rows="4"
                  maxlength="2000"
                />
              </div>
              <div class="form-group">
                <label for="campaign-link">Internal link</label>
                <input
                  id="campaign-link"
                  v-model="form.link"
                  class="form-control"
                  maxlength="255"
                  placeholder="/explore"
                />
                <small>Optional. Use an internal path starting with /.</small>
              </div>
            </section>
            <section class="campaign-panel campaign-form editor-section">
              <div class="editor-section-heading">
                <span class="editor-section-icon">
                  <i class="fa fa-tag" aria-hidden="true" />
                </span>
                <div>
                  <h2>Linked promotion</h2>
                  <p>Choose the discount behind this campaign.</p>
                </div>
              </div>
              <div class="form-group">
                <label for="promotion-search">Search promotion codes</label>
                <div class="input-group">
                  <input
                    id="promotion-search"
                    v-model="search"
                    class="form-control"
                    placeholder="Search by code"
                    @keydown.enter.prevent="searchPromotions"
                  />
                  <div class="input-group-append">
                    <button
                      type="button"
                      class="btn btn-outline-primary"
                      :disabled="searchLoading"
                      @click="searchPromotions"
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="promotionErrors.length" class="alert alert-danger" role="alert">
                <div v-for="(error, i) in promotionErrors" :key="i">{{ error }}</div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="loadPromotions"
                >
                  Retry
                </button>
              </div>
              <div class="form-group">
                <label for="campaign-promotion">Promotion code *</label>
                <select
                  id="campaign-promotion"
                  v-model.number="form.promotion_code_id"
                  class="form-control"
                  required
                  @change="loadPromotion"
                >
                  <option :value="null" disabled>Select a promotion code</option>
                  <option
                    v-if="
                      form.promotion_code_id &&
                      !options.some((item) => item.id === form.promotion_code_id)
                    "
                    :value="form.promotion_code_id"
                  >
                    {{ promotion ? promotion.code : '#' + form.promotion_code_id }}
                  </option>
                  <option v-for="option in options" :key="option.id" :value="option.id">
                    {{ option.code }} (#{{ option.id }})
                  </option>
                </select>
                <small v-if="searchLoading">Loading codes…</small>
                <small v-else-if="!options.length">No matching codes found.</small>
                <div class="campaign-actions mt-2">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="searchLoading || codePage <= 1"
                    @click="changeCodePage(-1)"
                  >
                    Previous
                  </button>
                  <span>Page {{ codePage }} of {{ codeLastPage }}</span>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="searchLoading || codePage >= codeLastPage"
                    @click="changeCodePage(1)"
                  >
                    Next
                  </button>
                </div>
              </div>
              <p v-if="detailLoading">Loading promotion details…</p>
              <div v-if="detailErrors.length" class="alert alert-danger" role="alert">
                <div v-for="(error, i) in detailErrors" :key="i">{{ error }}</div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="loadPromotion"
                >
                  Retry details
                </button>
              </div>
              <div v-if="promotion" class="campaign-preview editor-promotion mb-3">
                <strong>{{ promotion.code }}</strong>
                <p class="mb-1">{{ discount(promotion) }}</p>
                <p class="mb-1">
                  Starts: {{ promotion.start_at || 'Not set' }}
                  <br />
                  Expires: {{ promotion.expire_at || 'Not set' }}
                </p>
                <p>
                  Used
                  {{
                    promotion.used_carts_count == null ? '—' : promotion.used_carts_count
                  }}
                  times · Limit:
                  {{
                    promotion.limit_usage == null ? 'Unlimited' : promotion.limit_usage
                  }}
                </p>
                <router-link
                  :to="{ name: 'promotion.edit', params: { id: promotion.id } }"
                >
                  Edit promotion code
                </router-link>
              </div>
              <p>
                Discount values, dates and product restrictions are managed on the linked
                promotion code. Stored promotion dates use UTC.
              </p>
            </section>
          </div>
          <div class="col-lg-5">
            <section class="campaign-panel campaign-form editor-section">
              <div class="editor-section-heading">
                <span class="editor-section-icon">
                  <i class="fa fa-sliders" aria-hidden="true" />
                </span>
                <div>
                  <h2>Display settings</h2>
                  <p>Choose when and where your offer appears.</p>
                </div>
              </div>
              <div
                class="form-check editor-placement"
                :class="{ 'is-selected': form.enabled }"
              >
                <input
                  id="campaign-enabled"
                  v-model="form.enabled"
                  type="checkbox"
                  class="form-check-input"
                />
                <label for="campaign-enabled" class="form-check-label">
                  Enable campaign
                  <small>Make this campaign available to customers</small>
                </label>
              </div>
              <div
                class="form-check editor-placement"
                :class="{ 'is-selected': form.show_banner }"
              >
                <input
                  id="campaign-banner"
                  v-model="form.show_banner"
                  type="checkbox"
                  class="form-check-input"
                />
                <label for="campaign-banner" class="form-check-label">
                  Show banner
                  <small>A promotional strip on the website</small>
                </label>
              </div>
              <div
                class="form-check editor-placement"
                :class="{ 'is-selected': form.show_popup }"
              >
                <input
                  id="campaign-popup"
                  v-model="form.show_popup"
                  type="checkbox"
                  class="form-check-input"
                />
                <label for="campaign-popup" class="form-check-label">
                  Show pop-up
                  <small>A focused offer shown once per session</small>
                </label>
              </div>
              <p
                v-if="form.enabled && !form.show_banner && !form.show_popup"
                class="alert alert-warning"
              >
                No placement is enabled. This campaign will not appear.
              </p>
              <div class="form-group">
                <label for="campaign-delay">Pop-up delay (seconds)</label>
                <input
                  id="campaign-delay"
                  v-model.number="form.popup_delay_seconds"
                  type="number"
                  min="0"
                  max="3600"
                  step="1"
                  required
                  class="form-control"
                />
                <small>0–3600 seconds. Display frequency: once per session.</small>
              </div>
              <div class="form-group">
                <label for="campaign-priority">Priority</label>
                <input
                  id="campaign-priority"
                  v-model.number="form.priority"
                  type="number"
                  min="-1000"
                  max="1000"
                  step="1"
                  required
                  class="form-control"
                />
                <small>Higher priority wins; the newest campaign wins ties.</small>
              </div>
              <p>
                Enabled campaigns still require an eligible promotion code with remaining
                usage.
              </p>
            </section>
            <section class="campaign-panel editor-section editor-preview-wrap">
              <div class="editor-preview-heading">
                <span>
                  <i class="fa fa-eye" aria-hidden="true" />
                  Content preview
                </span>
                <span class="editor-status" :class="{ 'is-enabled': form.enabled }">
                  {{ form.enabled ? 'Enabled' : 'Disabled' }}
                </span>
              </div>
              <div class="campaign-preview editor-offer-preview">
                <span class="editor-preview-eyebrow">A little more extraordinary</span>
                <div v-if="promotion" class="editor-preview-discount">
                  {{ discount(promotion) }}
                </div>
                <h2 class="mt-2">{{ form.title || 'Campaign title' }}</h2>
                <p>{{ form.description || 'Campaign description' }}</p>
                <div class="editor-coupon">
                  <small>YOUR PROMOTION CODE</small>
                  <strong>{{ promotion ? promotion.code : 'Promotion code' }}</strong>
                </div>
                <small v-if="form.link" class="d-block mt-2">
                  Destination: {{ form.link }}
                </small>
              </div>
            </section>
          </div>
        </div>
        <div class="campaign-actions editor-save-bar">
          <div class="editor-save-note">
            <strong>
              {{
                isEdit
                  ? 'Ready to update your campaign?'
                  : 'Ready to launch your campaign?'
              }}
            </strong>
            <small>
              {{
                form.enabled
                  ? 'Your campaign will be enabled when saved.'
                  : 'Your campaign will be saved as disabled.'
              }}
            </small>
          </div>
          <button class="btn btn-primary" type="submit">
            {{ saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create campaign' }}
          </button>
          <router-link :to="{ name: 'campaigns' }">Cancel</router-link>
        </div>
      </fieldset>
    </form>
    <translator
      ref="translator"
      model="Campaign"
      :keys="translateKeys"
      :allowed-languages="['ar']"
      v-if="ready && isEdit"
      :key="$route.params.id"
      :model_id="Number($route.params.id)"
    />
  </div>
</template>
<script>
import {
  axios,
  campaignUrl,
  promotionUrl,
  errorsFrom,
  defaults,
  payload,
  validLink,
} from './api';
import Translator from '@/components/Translator.vue';
export default {
  name: 'CampaignForm',
  components: { Translator },
  data: () => ({
    translateKeys: [
      { key: 'title', type: 'text' },
      { key: 'description', type: 'text' },
    ],
    form: defaults(),
    ready: false,
    loading: false,
    saving: false,
    errors: [],
    promotion: null,
    search: '',
    options: [],
    codePage: 1,
    codeLastPage: 1,
    searchLoading: false,
    promotionErrors: [],
    detailLoading: false,
    detailErrors: [],
    searchRequest: 0,
    detailRequest: 0,
    formRequest: 0,
  }),
  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },
  },
  mounted() {
    this.initialize();
  },
  watch: {
    '$route.params.id'() {
      this.initialize();
    },
  },
  methods: {
    discount(p) {
      return p.type === 'Percent'
        ? Number((Number(p.value) * 100).toFixed(2)) + '% off'
        : p.value + ' AED off';
    },
    async initialize() {
      const request = ++this.formRequest;
      this.detailRequest++;
      this.detailErrors = [];
      this.detailLoading = false;
      this.form = defaults();
      this.promotion = null;
      this.ready = false;
      this.loading = false;
      this.errors = [];
      this.search = '';
      this.codePage = 1;
      this.loadPromotions();
      if (!this.isEdit) {
        this.ready = true;
        return;
      }
      this.loading = true;
      try {
        const { data } = await axios.get(campaignUrl() + '/' + this.$route.params.id, {
          params: { language: 'en' },
        });
        if (request !== this.formRequest) return;
        Object.keys(defaults()).forEach((key) => {
          if (data.data[key] !== undefined) this.form[key] = data.data[key];
        });
        this.form.link = this.form.link || '';
        this.form.description = this.form.description || '';
        this.promotion = data.data.promotion_code;
        this.ready = true;
      } catch (error) {
        if (request === this.formRequest) this.errors = errorsFrom(error);
      } finally {
        if (request === this.formRequest) this.loading = false;
      }
    },
    searchPromotions() {
      this.codePage = 1;
      this.loadPromotions();
    },
    changeCodePage(delta) {
      this.codePage += delta;
      this.loadPromotions();
    },

    async loadPromotions() {
      const request = ++this.searchRequest;
      this.searchLoading = true;
      this.promotionErrors = [];
      try {
        const { data } = await axios.get(promotionUrl(), {
          params: { search: this.search, page: this.codePage },
        });
        if (request !== this.searchRequest) return;
        this.options = data.data;
        this.codeLastPage = data.meta.last_page;
      } catch (error) {
        if (request === this.searchRequest) this.promotionErrors = errorsFrom(error);
      } finally {
        if (request === this.searchRequest) this.searchLoading = false;
      }
    },
    async loadPromotion() {
      const request = ++this.detailRequest;
      this.promotion = null;
      this.detailLoading = true;
      this.detailErrors = [];
      try {
        const { data } = await axios.get(
          promotionUrl() + '/' + this.form.promotion_code_id
        );
        if (request === this.detailRequest) this.promotion = data.data;
      } catch (error) {
        if (request === this.detailRequest) this.detailErrors = errorsFrom(error);
      } finally {
        if (request === this.detailRequest) this.detailLoading = false;
      }
    },
    async save() {
      if (this.saving) return;
      this.errors = [];
      const body = payload(this.form);
      if (!body.title) {
        this.errors = ['Enter a campaign title.'];
        return;
      }
      if (!validLink(body.link)) {
        this.errors = [
          'Use an internal path starting with a single /. Spaces and backslashes are not allowed.',
        ];
        return;
      }
      this.saving = true;
      try {
        const options = { params: { language: 'en' } };
        const response = this.isEdit
          ? await axios.put(campaignUrl() + '/' + this.$route.params.id, body, options)
          : await axios.post(campaignUrl(), body, options);
        this.$notify({ group: 'notify', type: 'success', title: 'Campaign saved' });
        if (!this.isEdit)
          this.$router.push({
            name: 'campaign.edit',
            params: { id: response.data.data.id },
          });
      } catch (error) {
        this.errors = errorsFrom(error);
        this.$nextTick(() => {
          const alert = this.$el.querySelector('[role="alert"]');
          if (alert) alert.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style src="./campaigns.css"></style>
